/**
 * Le briefing d'une intervention (lot U1c, ADR-0016) : ce qu'il faut savoir avant d'y aller, LU chez son propriétaire.
 *
 * Lieu, machine et nature viennent du Parc ; les jours et l'équipe de l'intervention ; les lignes prévues (lignes enregistrées,
 * sinon pièces du palier lues dans la gamme, avec la raison quand il n'y en a pas). Aucun prix. Cette tranche LIT : les gestes
 * d'écriture viennent avec la suivante. Les erreurs ont leur propre état (jamais effacées par un rechargement).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type BriefingIntervention } from '../api/client';
import { useDroitsBons } from '../auth/useDroits';
import { aujourdhuiLocal, jourFr } from '../domaine/interventions';
import { jourLong } from '../domaine/planning';
import { BlocTourneeIntervention } from './BlocTourneeIntervention';
import { PastilleEtat } from './LegendeEtats';
import { ForfaitTournee } from './ForfaitTournee';
import { CartePointages } from './CartePointages';
import { CorrectionsHeuresIntervention } from './CorrectionsHeuresIntervention';
import { membresPointables } from '../domaine/pointages';
import { LignesIntervention } from './LignesIntervention';
import { ModifierIntervention } from './ModifierIntervention';
import { PalierIntervention } from './PalierIntervention';
import { CreneauJour } from './CreneauJour';
import { SaisieHeuresIntervention } from './SaisieHeuresIntervention';
import { etatDuBon, libelleMachine, libelleSuite, lienDuBon, lienIntervention, partValidee, phraseBonsValides, motifValide, numeroAffiche, interventionFigee, peutConfirmer, peutCorrigerLesHeures, peutDeplacer, peutSaisirDesHeures, phraseConfirmation, phraseHeuresMembre, titreIntervention } from '../domaine/planning-interventions';

export function BriefingInterventionCarte({
  interventionId,
  rechargement,
  peutGerer,
  onChange,
  onMessage,
}: {
  interventionId: string;
  rechargement: number;
  peutGerer: boolean;
  /** Une écriture a réussi : la page relit la semaine et la liste. */
  onChange: () => void;
  onMessage: (message: string) => void;
}) {
  const [briefing, setBriefing] = useState<BriefingIntervention | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [annulation, setAnnulation] = useState<{ motif: string; enCours: boolean } | null>(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);
  /** Le panneau « Modifier l'intervention » est ouvert. */
  const [modification, setModification] = useState(false);
  const droitsBons = useDroitsBons();
  /** Le refus du geste : à part de `erreur` (la lecture), que la relecture qui suit ne doit pas effacer. */
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const entete = useRef<HTMLDivElement>(null);
  // Un refus venu du bas du détail (les lignes) se dit dans l'en-tête : on y ramène la vue pour qu'il ne passe pas inaperçu.
  useEffect(() => {
    if (erreurGeste !== null) entete.current?.scrollIntoView({ block: 'nearest' });
  }, [erreurGeste]);

  useEffect(() => {
    let annule = false;
    api
      .planningBriefingIntervention(interventionId)
      .then((b) => {
        if (annule) return;
        setBriefing(b);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement de l’intervention impossible.');
      });
    return () => {
      annule = true;
    };
  }, [interventionId, rechargement]);

  if (erreur) {
    return (
      <div className="carte">
        <div className="message erreur">{erreur}</div>
      </div>
    );
  }
  if (!briefing) {
    return (
      <div className="carte">
        <div className="vide">Chargement…</div>
      </div>
    );
  }

  const i = briefing.intervention;
  const ouverte = peutDeplacer(i, peutGerer);
  const lienBon = lienDuBon(i, droitsBons.peutSaisirBons);
  const annuler = async () => {
    if (annulation === null) return;
    const motif = motifValide(annulation.motif);
    if (motif === null) {
      setErreurGeste("Donnez un motif d’au moins trois caractères.");
      return;
    }
    setAnnulation({ ...annulation, enCours: true });
    setErreurGeste(null);
    try {
      await api.annulerIntervention(i.id, i.revision, motif);
      setAnnulation(null);
      onMessage(`Intervention ${numeroAffiche(i)} annulée : son numéro est conservé.`);
      onChange();
    } catch (e) {
      setAnnulation({ ...annulation, enCours: false });
      // 409 : l’intervention a changé depuis l’affichage. On relit, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreurGeste(e instanceof ApiError || e instanceof Error ? e.message : "Annulation impossible.");
    }
  };

  const confirmer = async () => {
    setConfirmationEnCours(true);
    setErreurGeste(null);
    try {
      const bon = await api.confirmerIntervention(i.id, i.revision);
      onMessage(phraseConfirmation(bon.number));
      onChange();
    } catch (e) {
      // 409 : l'intervention a changé depuis l'affichage. On relit, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreurGeste(e instanceof ApiError || e instanceof Error ? e.message : 'Confirmation impossible.');
    } finally {
      setConfirmationEnCours(false);
    }
  };

  return (
    <div className="carte briefing-visite">
      <div className="briefing-entete" ref={entete}>
        <h2>
          {titreIntervention(i)} <PastilleEtat status={i.status} />
        </h2>
        <p className="aide">
          {numeroAffiche(i)}
          {i.natureLabel ? ` · ${i.natureLabel}` : ''}
          {i.tentative ? ' · à confirmer' : ''}
          {i.creeeSurLeTerrain ? ' · créée sur le terrain par le technicien (contrôlée à la validation)' : ''}
        </p>
      </div>

      {erreurGeste && <div className="message erreur">{erreurGeste}</div>}
      {lienBon && (
        <div className="saisie-ligne">
          <Link className={lienBon.principal ? 'bouton-lien' : 'bouton-lien secondaire-lien'} to={lienBon.href}>
            {lienBon.libelle}
          </Link>
          <span className="aide">
            {lienBon.principal
              ? 'Le bon se remplit sur cette intervention : le jour et l’équipe prévus sont déjà proposés. Faite = bon soumis.'
              : i.status === 'TO_REVIEW'
                ? 'Le bon est soumis : l’intervention attend son contrôle. Rouvrez-le depuis le bon pour le corriger.'
                : 'Le bon est validé.'}
          </span>
        </div>
      )}
      {ouverte && (
        <div className="saisie-ligne">
          {annulation === null && (
            <button type="button" className="secondaire" onClick={() => setModification(true)} title="Corriger la nature, les jours, l’équipe, la durée, la demande du client…">
              ✎ Modifier
            </button>
          )}
          {peutConfirmer(i, peutGerer) && annulation === null && (
            <button type="button" disabled={confirmationEnCours} onClick={() => void confirmer()} title="Donne son numéro à l’intervention (une occurrence de tournée n’en a pas avant)">
              Confirmer (donner un numéro)
            </button>
          )}
          {annulation === null ? (
            <button type="button" className="secondaire" onClick={() => setAnnulation({ motif: "", enCours: false })}>
              Annuler l’intervention
            </button>
          ) : (
            <>
              <input
                type="text"
                aria-label="Motif de l’annulation"
                placeholder="Motif de l’annulation (obligatoire)"
                value={annulation.motif}
                onChange={(e) => setAnnulation({ ...annulation, motif: e.target.value })}
              />
              <button type="button" disabled={annulation.enCours} onClick={() => void annuler()}>
                Confirmer l’annulation
              </button>
              <button type="button" className="secondaire" disabled={annulation.enCours} onClick={() => setAnnulation(null)}>
                Garder
              </button>
            </>
          )}
        </div>
      )}

      {modification && (
        <ModifierIntervention
          briefing={briefing}
          onFermer={() => setModification(false)}
          onModifiee={(message) => {
            setModification(false);
            onMessage(message);
            onChange();
          }}
          onConflit={onChange}
        />
      )}

      <dl className="briefing-faits">
        {i.site && (
          <>
            <dt>Lieu</dt>
            <dd>{i.site.name}</dd>
          </>
        )}
        <dt>Machine</dt>
        <dd>
          {i.asset ? <Link to={`/parc?asset=${i.asset.id}`}>{libelleMachine(i)}</Link> : libelleMachine(i)}
        </dd>
        {i.customerOrderRef && (
          <>
            <dt>N° de commande</dt>
            <dd>{i.customerOrderRef}</dd>
          </>
        )}
        {i.description && (
          <>
            <dt>Demande</dt>
            <dd>{i.description}</dd>
          </>
        )}
      </dl>

      {briefing.machine && (
        <div className="planning-machine">
          {briefing.machine.derniersBons.length > 0 ? (
            <p className="aide">
              Derniers bons :{' '}
              {briefing.machine.derniersBons.map((b, k) => (
                <span key={b.id}>
                  {k > 0 && ' · '}
                  <Link to={`/interventions?bon=${b.id}`}>{b.number ?? 'bon'}</Link>
                  {b.performedOn ? ` du ${jourFr(b.performedOn)}` : ''}
                  {b.natureLabel ? ` (${b.natureLabel})` : ''}
                </span>
              ))}
            </p>
          ) : null}
          {briefing.machine.suites.length > 0 ? (
            <p className="aide">
              À traiter sur cette machine :{' '}
              {briefing.machine.suites.map((s, k) => (
                <span key={s.id}>
                  {k > 0 && ' · '}
                  {libelleSuite(s.kind)}
                </span>
              ))}{' '}
              — <Link to={`/suites?machine=${i.asset?.id ?? ''}`}>voir</Link>
            </p>
          ) : null}
          {briefing.machine.derniersBons.length === 0 && briefing.machine.suites.length === 0 ? (
            <p className="aide">Aucun bon validé ni suite à donner pour l’instant.</p>
          ) : null}
        </div>
      )}

      {briefing.groupe && (
        <div className="bloc-bons">
          <h3>Même passage</h3>
          <p>
            <strong>{phraseBonsValides(briefing.groupe.valides, briefing.groupe.total)}</strong>
          </p>
          <div
            className="barre-avancement"
            role="progressbar"
            aria-label="Bons validés"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={partValidee(briefing.groupe.valides, briefing.groupe.total)}
          >
            <span style={{ width: `${partValidee(briefing.groupe.valides, briefing.groupe.total)}%` }} />
          </div>
          <ul className="bons-visite">
            {briefing.groupe.lignes.map((l) => {
              const etat = etatDuBon(l.status);
              return (
                <li key={l.id}>
                  <span>{l.machineLabel ?? 'Machine à identifier'}</span>
                  {l.id === i.id ? <span className="aide">celle-ci</span> : <Link to={lienIntervention(l.id, null)}>{l.number ?? 'prévue'}</Link>}
                  <span className={`etat ${etat.classe}`}>{etat.libelle}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {briefing.consignesSite && (
        <div className="consignes-site">
          <h3>Ce que le site demande</h3>
          <dl className="briefing-faits">
            {briefing.consignesSite.accessNotes && (
              <>
                <dt>Accès</dt>
                <dd className="observations-bon">{briefing.consignesSite.accessNotes}</dd>
              </>
            )}
            {briefing.consignesSite.openingHours && (
              <>
                <dt>Horaires</dt>
                <dd className="observations-bon">{briefing.consignesSite.openingHours}</dd>
              </>
            )}
            {briefing.consignesSite.safetyNotes && (
              <>
                <dt>Sécurité</dt>
                <dd className="observations-bon">{briefing.consignesSite.safetyNotes}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {briefing.tour && (
        <BlocTourneeIntervention tour={briefing.tour} interventionId={i.id} peutGerer={peutGerer} revision={i.revision} onChange={onChange} />
      )}
      {briefing.tour && <ForfaitTournee tourId={briefing.tour.id} arretee={briefing.tour.stopped} peutGerer={peutGerer} onChange={onChange} />}

      <h3>Jours et équipe</h3>
      {briefing.jours.length === 0 ? (
        <p className="aide">Aucun jour travaillé n’est encore prévu : l’intervention attend dans la liste « À affecter ».</p>
      ) : (
        <ul className="briefing-jours">
          {briefing.jours.map((j) => (
            <li key={j.day}>
              <strong>{jourLong(j.day)}</strong>
              <CreneauJour
                interventionId={i.id}
                revision={i.revision}
                jour={j.day}
                slot={j.slot}
                appointmentTime={j.appointmentTime}
                peutGerer={ouverte}
                onChange={onChange}
                onMessage={onMessage}
              />
              {j.members.length === 0 ? (
                <span className="aide"> — équipe à choisir</span>
              ) : (
                <span>
                  {' — '}
                  {j.members
                    .map((m) => {
                      const heures = phraseHeuresMembre(m);
                      return `${m.name}${m.isLead ? ' (chef)' : ''}${heures === '' ? '' : `, ${heures}`}`;
                    })
                    .join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {peutSaisirDesHeures(i, peutGerer) && (
        <div id="saisie-heures">
          <SaisieHeuresIntervention interventionId={i.id} revision={i.revision} jours={briefing.jours} onChange={onChange} onMessage={onMessage} />
        </div>
      )}

      {/* Registre B34 : les heures pointées par les techniciens, vérifiées et corrigées ici. */}
      <CartePointages
        interventionId={i.id}
        pointages={briefing.pointages}
        membres={membresPointables(briefing.jours, aujourdhuiLocal())}
        peutGerer={peutGerer && !interventionFigee(i) && i.status !== 'CANCELLED'}
        onChange={onChange}
      />

      {interventionFigee(i) && (
        <CorrectionsHeuresIntervention
          interventionId={i.id}
          jours={briefing.jours}
          peutCorriger={peutCorrigerLesHeures(i, droitsBons.peutValiderBons)}
          rechargement={rechargement}
          onChange={onChange}
          onMessage={onMessage}
        />
      )}

      <PalierIntervention
        interventionId={i.id}
        assetId={i.asset?.id ?? null}
        revision={i.revision}
        plannedLevelKey={briefing.plannedLevelKey}
        gamme={briefing.gamme}
        peutDeplacer={ouverte}
        onChange={onChange}
        onMessage={onMessage}
      />

      <LignesIntervention
        briefing={briefing}
        rechargement={rechargement}
        peutGerer={peutGerer}
        peutModifierLesHeures={peutSaisirDesHeures(i, peutGerer)}
        onChange={onChange}
        onErreur={setErreurGeste}
      />
    </div>
  );
}
